import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  PhoneIcon,
  PhoneXMarkIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  UserIcon,
  XMarkIcon,
  ListBulletIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { campaignStatuses } from '../../mocks/campaigns';
import { callStatuses, callOutcomes, formatDuration, formatTime } from '../../mocks/calls';
import { getCampaign as fetchCampaign, updateCampaignStatus, listContacts, deleteCampaign, getContactCallData, exportCampaignCallLog, getTenantOutcomeOptions, uploadContactsFile } from '../../services/campaignApi';
import { cn } from '../../lib/utils';
import WhatsAppThreadPanel from '../whatsapp/WhatsAppThreadPanel';
import toast from 'react-hot-toast';

// Resolve a contact's display duration: prefer the top-level call_duration,
// then fall back to the latest non-zero call_history[].duration. The top-level
// field can land as 0 when the real Twilio CallDuration arrives on a late
// 'completed' callback, so the call_history fallback recovers a true duration
// before the row renders '-'.
function resolveContactDuration(c) {
  const top = c.call_duration ?? c.duration;
  if (top) return top;
  const hist = Array.isArray(c.call_history) ? c.call_history : [];
  for (let i = hist.length - 1; i >= 0; i--) {
    const d = hist[i]?.duration;
    if (d) return d;
  }
  return 0;
}

// Normalize a raw phone string to E.164 ("+<digits>") so it matches the
// backend `lead_phone` thread key used by the WhatsApp endpoints.
function normalizeE164(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

// Status badge component
const StatusBadge = ({ status, t }) => {
  const config = campaignStatuses[status] || campaignStatuses.draft;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
        config.color,
        config.pulse && 'animate-pulse'
      )}
    >
      {status === 'running' && <span className="w-2 h-2 bg-green-500 rounded-full" />}
      {t(config.labelKey)}
    </span>
  );
};

// KPI Card component
const KPICard = ({ icon: Icon, label, value, subValue, color, trend }) => (
  <div className="bg-card rounded-xl border border-border p-4">
    <div className="flex items-start justify-between">
      <div
        className="p-2 rounded-lg"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      {trend && (
        <span className={cn(
          'text-xs font-medium px-2 py-0.5 rounded-full',
          trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        )}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-semibold text-foreground">{value}</p>
      <p className="text-sm text-muted mt-0.5">{label}</p>
      {subValue && (
        <p className="text-xs text-muted mt-1">{subValue}</p>
      )}
    </div>
  </div>
);

// Progress bar component
const CampaignProgress = ({ current, total, status, t }) => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  const isActive = status === 'running';

  return (
    <div className="bg-card rounded-xl border border-border p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-foreground">{t('campaigns.progress')}</span>
        <span className="text-sm font-semibold text-foreground">{percentage}%</span>
      </div>
      <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            isActive ? 'bg-green-500' : 'bg-primary'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-2 text-xs text-muted">
        <span>{t('campaigns.callsPlaced', { count: current.toLocaleString() })}</span>
        <span>{t('campaigns.totalContacts', { count: total.toLocaleString() })}</span>
      </div>
    </div>
  );
};

// Status filter options, de-duplicated by display label. `callStatuses` carries
// Twilio aliases that share a label with a canonical key ('no-answer' -> no_answer,
// 'ended' -> completed), so mapping every entry to an <option> listed "No Answer"
// and "Completed" twice (Oct-10 Amplify audit, shots/22-campaign-detail.jpg).
// One option per labelKey; the filter below matches ANY alias of the chosen key.
const STATUS_FILTER_OPTIONS = Object.entries(callStatuses).reduce((acc, [key, cfg]) => {
  if (!acc.some(o => o.labelKey === cfg.labelKey)) acc.push({ key, labelKey: cfg.labelKey });
  return acc;
}, []);
const statusMatchesFilter = (status, filterKey) => {
  if (status === filterKey) return true;
  const want = callStatuses[filterKey]?.labelKey;
  const have = callStatuses[status]?.labelKey;
  return Boolean(want) && want === have;
};

// Call status badge
const CallStatusBadge = ({ status, t }) => {
  const config = callStatuses[status] || callStatuses.completed;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', config.color)}>
      {status === 'calling' && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />}
      {t(config.labelKey)}
    </span>
  );
};

// Call outcome badge — supports both outcome key (legacy) and outcome_label (new structured)
const CallOutcomeBadge = ({ outcome, outcomeLabel, t }) => {
  if (!outcome) return <span className="text-xs text-muted">-</span>;
  const config = callOutcomes[outcome] || { labelKey: `callOutcomes.${outcome}`, color: 'bg-secondary text-foreground' };
  // Prefer the human-readable label from the API if available
  const displayText = outcomeLabel || t(config.labelKey);
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', config.color)}>
      {displayText}
    </span>
  );
};

// Call log table
const CallLogTable = ({ calls, hasCampaignCalls, onCallClick, loading, highlightedContactId, selectedContactId, t }) => {
  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse flex items-center gap-4 p-3 bg-background rounded-xl">
            <div className="h-4 bg-secondary rounded w-24" />
            <div className="h-4 bg-secondary rounded w-20" />
            <div className="h-4 bg-secondary rounded w-28" />
            <div className="h-4 bg-secondary rounded w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="text-center py-12 text-muted">
        <PhoneIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>{t(hasCampaignCalls ? 'campaigns.noMatchingCalls' : 'campaigns.noCallsYet')}</p>
        {!hasCampaignCalls && <p className="text-xs mt-1">{t('campaigns.callsAppearHere')}</p>}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-background">
            <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">{t('campaigns.contact')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">{t('common.status')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">{t('campaigns.outcome')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">{t('campaigns.duration')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">{t('campaigns.time')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">{t('campaigns.attempts')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">{t('campaigns.callerId', 'Caller ID')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {calls.map((call) => {
            return (
            <tr
              key={call.id}
              id={`contact-row-${call.contactId}`}
              className={cn(
                'hover:bg-hover cursor-pointer transition-colors duration-700',
                ((highlightedContactId && call.contactId === highlightedContactId) || (selectedContactId && call.contactId === selectedContactId)) && 'ring-2 ring-primary ring-inset bg-primary/20'
              )}
              onClick={() => onCallClick(call)}
            >
              <td className="px-4 py-3">
                {call.name ? (
                  <div>
                    <span className="text-sm font-medium text-foreground">{call.name}</span>
                    <span className="block text-xs text-muted font-mono">{call.phone}</span>
                  </div>
                ) : (
                  <span className="font-mono text-sm text-foreground">{call.phone}</span>
                )}
              </td>
              <td className="px-4 py-3">
                <CallStatusBadge status={call.status} t={t} />
              </td>
              <td className="px-4 py-3">
                <CallOutcomeBadge outcome={call.outcome} outcomeLabel={call.outcome_label} t={t} />
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-muted">
                  {call.duration ? formatDuration(call.duration) : '-'}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-muted">{formatTime(call.startedAt)}</span>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm text-muted font-mono">
                  {call.maxRetries > 0 ? `${call.attempts}/${call.maxRetries}` : call.attempts}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className="text-xs text-muted font-mono">{call.usedCallerId || '-'}</span>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Call Detail Drawer
const CallDetailDrawer = ({ call, campaignId, onClose, t }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [callData, setCallData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (!call?.contactId || !campaignId) return;
    let cancelled = false;
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const res = await getContactCallData(campaignId, call.contactId);
        if (!cancelled) setCallData(res.data);
      } catch (err) {
        console.error('Failed to load call data:', err);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [call?.contactId, campaignId]);

  if (!call) return null;

  const transcript = [...(callData?.transcript || [])].sort((a, b) => {
    if (!a.timestamp && !b.timestamp) return (a.seq ?? 0) - (b.seq ?? 0);
    if (!a.timestamp) return 1;
    if (!b.timestamp) return -1;
    if (a.timestamp !== b.timestamp) return a.timestamp < b.timestamp ? -1 : 1;
    if (a.seq != null && b.seq != null) return a.seq - b.seq;
    return a.speaker === 'user' ? -1 : 1;
  });
  const summary = callData?.summary;
  const outcomeData = callData?.outcome_data;

  // Derive events from transcript timestamps
  const events = [];
  if (callData?.start_time) events.push({ details: t('campaigns.callStarted'), timestamp: callData.start_time });
  if (transcript.length > 0) events.push({ details: t('campaigns.firstInteraction'), timestamp: transcript[0]?.timestamp });
  if (callData?.outcome) events.push({ details: t('campaigns.resultPrefix', { result: callData.outcome }), timestamp: callData.outcome_data?.created_at || transcript[transcript.length - 1]?.timestamp });
  if (callData?.end_time) events.push({ details: t('campaigns.callEnded'), timestamp: callData.end_time });

  const tabs = [
    { id: 'summary', label: t('campaigns.summary'), icon: ListBulletIcon },
    { id: 'transcript', label: t('campaigns.transcript'), icon: ChatBubbleLeftRightIcon },
    { id: 'events', label: t('campaigns.events'), icon: ClockIcon },
    { id: 'whatsapp', label: t('campaigns.whatsapp'), icon: ChatBubbleOvalLeftEllipsisIcon },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop — transparent + click-through so the deep-linked left-list
          row (scroll-to + highlight ring) stays fully visible while the drawer
          is open. inset-0 + onClick preserves backdrop-click-to-close. */}
      <div
        className="absolute inset-0 bg-transparent"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="relative w-full max-w-lg bg-card shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {call.name || t('campaigns.callDetails')}
            </h3>
            <p className="text-sm text-muted font-mono">{call.phone}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-hover rounded-full transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-muted" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-foreground'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingData && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          )}

          {!loadingData && activeTab === 'summary' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted uppercase mb-1">{t('common.status')}</p>
                  <CallStatusBadge status={call.status} t={t} />
                </div>
                <div>
                  <p className="text-xs text-muted uppercase mb-1">{t('campaigns.outcome')}</p>
                  <CallOutcomeBadge outcome={callData?.outcome || call.outcome} outcomeLabel={callData?.outcome_label || call.outcome_label} t={t} />
                </div>
                <div>
                  <p className="text-xs text-muted uppercase mb-1">{t('campaigns.duration')}</p>
                  <p className="text-sm font-medium text-foreground">
                    {callData?.duration ? formatDuration(callData.duration) : (call.duration ? formatDuration(call.duration) : '-')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted uppercase mb-1">{t('campaigns.startedAt')}</p>
                  <p className="text-sm font-medium text-foreground">{formatTime(call.startedAt)}</p>
                </div>
              </div>

              {/* AI Summary or outcome summary as fallback */}
              {(summary || callData?.outcome_summary) && (
                <div className="mt-4 p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
                  <h4 className="text-sm font-semibold text-indigo-900 mb-2">{t('campaigns.aiSummary')}</h4>
                  <p className="text-sm text-indigo-800 whitespace-pre-line">
                    {summary || callData?.outcome_summary}
                  </p>
                </div>
              )}

              {outcomeData && (
                <div className="mt-3 space-y-3">
                  {outcomeData.confidence && (
                    <div>
                      <p className="text-xs text-muted uppercase mb-1">{t('campaigns.confidence')}</p>
                      <span className={cn(
                        'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                        (outcomeData.confidence === 'high' || outcomeData.confidence === 'certain') ? 'bg-green-100 text-green-700' :
                        (outcomeData.confidence === 'medium' || outcomeData.confidence === 'likely') ? 'bg-yellow-100 text-yellow-700' :
                        'bg-secondary text-muted'
                      )}>
                        {(outcomeData.confidence === 'high' || outcomeData.confidence === 'certain') ? t('campaigns.confidenceHigh') :
                         (outcomeData.confidence === 'medium' || outcomeData.confidence === 'likely') ? t('campaigns.confidenceMedium') : t('campaigns.confidenceUncertain')}
                      </span>
                    </div>
                  )}

                  {outcomeData.commitments?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted uppercase mb-1">{t('campaigns.commitments')}</p>
                      <ul className="text-sm text-foreground space-y-1">
                        {outcomeData.commitments.map((c, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {typeof c === 'string'
                              ? c
                              : `${c.type || ''}${c.date ? ' \u2022 ' + c.date : ''}${c.details ? ' \u2014 ' + c.details : ''}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {outcomeData.evidence?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted uppercase mb-1">{t('campaigns.evidence')}</p>
                      <ul className="text-sm text-foreground space-y-1">
                        {outcomeData.evidence.map((e, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-primary">•</span>
                            <span className="italic text-muted">&quot;{e}&quot;</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4">
                <p className="text-xs text-muted uppercase mb-1">{t('campaigns.contactId')}</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-secondary px-2 py-1 rounded font-mono">{call.id}</code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(call.id);
                      toast.success(t('common.copied'));
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    {t('common.copy')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!loadingData && activeTab === 'transcript' && (
            <div className="space-y-3">
              {transcript.map((entry, i) => (
                <div key={i} className={cn(
                  'flex',
                  entry.speaker === 'assistant' ? 'justify-start' : 'justify-end'
                )}>
                  <div className={cn(
                    'max-w-[80%] rounded-lg px-4 py-2',
                    entry.speaker === 'assistant'
                      ? 'bg-green-50 dark:bg-green-950/40 border border-green-100 dark:border-green-800'
                      : 'bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-800'
                  )}>
                    <p className="text-xs text-muted mb-1">
                      {entry.speaker === 'assistant' ? t('campaigns.agentLabel') : t('campaigns.clientLabel')} • {formatTime(entry.timestamp)}
                    </p>
                    <p className="text-sm text-foreground">{typeof entry.text === 'string' ? entry.text : ''}</p>
                  </div>
                </div>
              ))}

              {transcript.length === 0 && (
                <div className="text-center py-8 text-muted">
                  <ChatBubbleLeftRightIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>{t('campaigns.noTranscript')}</p>
                  {call.status === 'queued' && <p className="text-xs mt-1">{t('campaigns.transcriptAppears')}</p>}
                </div>
              )}
            </div>
          )}

          {!loadingData && activeTab === 'events' && (
            <div className="space-y-3">
              {events.map((event, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{event.details}</p>
                    <p className="text-xs text-muted">{formatTime(event.timestamp)}</p>
                  </div>
                </div>
              ))}

              {events.length === 0 && (
                <div className="text-center py-8 text-muted">
                  <ClockIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>{t('campaigns.noEvents')}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'whatsapp' && (
            <WhatsAppThreadPanel
              leadPhone={normalizeE164(call.phone)}
              campaignId={campaignId}
              className="-m-6 h-full"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Main component
function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [campaign, setCampaign] = useState(null);
  const [calls, setCalls] = useState([]);
  // ANGLE 3 guard: one-shot keyed on the focus PARAM VALUE (not a never-reset
  // boolean and not DOM success). A new, different notification carries a new
  // focusContact/focusPhone key, so clicking a 2nd/3rd different notification
  // re-triggers the scroll+highlight; re-firing with the SAME key is a no-op.
  const lastFocusKeyRef = useRef(null);
  // Own the highlight-clear timer on a ref so effect cleanup (re-runs on `calls`
  // updates) can't kill an in-flight 3s fade before it completes.
  const highlightClearTimerRef = useRef(null);
  const [highlightedContactId, setHighlightedContactId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [attemptsFilter, setAttemptsFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [outcomeOptions, setOutcomeOptions] = useState(null); // per-tenant outcomes fetched from API
  const [uploadingLeads, setUploadingLeads] = useState(false);
  const addLeadsInputRef = React.useRef(null);

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || outcomeFilter !== 'all' || attemptsFilter !== 'all';

  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setOutcomeFilter('all');
    setAttemptsFilter('all');
  };

  // Deeplink: scroll to + highlight the LIST row for ?focusContact=<contact_id>
  // or ?focusPhone=<phone> (does NOT open the drawer — see note below).
  useEffect(() => {
    const focusId = searchParams.get('focusContact');
    const focusPhone = searchParams.get('focusPhone');
    // Gate on !loading too: while loading the table renders skeletons (no
    // contact-row-* elements exist), so scrolling can never land. Wait until the
    // real rows are mounted before attempting to focus.
    // Build the focus key from the param values. Empty params => no-op (handles the
    // URL-strip re-fire harmlessly). Accept only when the new non-empty key differs
    // from the last one we acted on.
    const focusKey = focusId || focusPhone || '';
    if (!focusKey || loading || calls.length === 0) return;
    if (lastFocusKeyRef.current === focusKey) return;
    // Normalize phone for tolerant comparison. We must match across common MX
    // phone-format variants because notifications and the contacts list don't
    // agree on shape:
    //   +52 9933730891  (landline / national form)
    //   +521 9933730891 (mobile form — extra '1' after the 52 country code)
    //   52 / 521 / 01 prefixes, spaces, dashes, parens, leading '+'.
    // A '+' round-tripped through a URL query string decodes to a space, so
    // ?focusPhone=+52... can arrive as ' 52...'; stripping non-digits handles that.
    // Canonicalize to the 10-digit national significant number (last 10 digits),
    // which reconciles +52 vs +521 and any country-code presence/absence.
    const normPhone = (p) => {
      const digits = (p || '').replace(/[^0-9]/g, '');
      if (!digits) return '';
      // Drop a Mexican mobile '1' that sits between the 52 country code and the
      // 10-digit number (52 1 XXXXXXXXXX -> 52 XXXXXXXXXX) before taking the tail.
      const deMobile = digits.replace(/^521(\d{10})$/, '52$1');
      // Compare on the national significant number: last 10 digits. This tolerates
      // presence/absence of the 52 country code and the 01 long-distance prefix.
      return deMobile.length > 10 ? deMobile.slice(-10) : deMobile;
    };
    const wantPhone = normPhone(focusPhone);
    // Match by contact_id first; fall back to phone so notifications whose payload
    // dropped contact_id (legacy/calls-table fallback) still open the exact lead.
    const match = calls.find((c) => {
      if (focusId && c.contactId === focusId) return true;
      if (wantPhone && normPhone(c.phone) === wantPhone) return true;
      return false;
    });
    if (!match) {
      // Root cause: the list is capped at limit:500, so a target lead beyond the
      // first 500 contacts never lands in calls[] and the matcher silently misses
      // (dead-click). Page past the 500 cap following last_key to locate the exact
      // target, then merge it into calls[] — that state update re-runs this effect
      // and the match succeeds on the next pass. Claim the key up-front so this
      // one-shot resolve can't loop across the effect re-runs it triggers.
      if (lastFocusKeyRef.current !== `resolving:${focusKey}`) {
        lastFocusKeyRef.current = `resolving:${focusKey}`;
        (async () => {
          let lastKey = null;
          for (let page = 0; page < 40; page++) {
            let res;
            try {
              res = await listContacts(id, { limit: 500, ...(lastKey ? { last_key: lastKey } : {}) });
            } catch (e) { break; }
            const found = (res.data?.contacts || []).find((c) =>
              (focusId && c.contact_id === focusId) ||
              (wantPhone && normPhone(c.phone) === wantPhone));
            if (found) {
              const mapped = {
                id: found.contact_id,
                contactId: found.contact_id,
                phone: found.phone,
                name: found.name || '',
                status: found.status === 'pending' ? 'queued' : found.status,
                outcome: found.last_outcome || found.latest_outcome || null,
                outcome_label: found.outcome_data?.outcome_label || null,
                outcome_summary: found.outcome_summary || found.outcome_data?.summary || '',
                duration: resolveContactDuration(found),
                attempts: found.call_attempts || 0,
                startedAt: found.created_at,
              };
              // Re-arm the key so the merge-driven effect re-run actually matches.
              lastFocusKeyRef.current = null;
              setCalls((prev) => prev.some((c) => c.contactId === found.contact_id) ? prev : [...prev, mapped]);
              return;
            }
            lastKey = res.data?.last_key || null;
            if (!lastKey) break;
          }
          console.warn('[NOTIF] deep-link target not found after paging past 500-cap', { focusId, focusPhone });
        })();
      }
      console.debug('[NOTIF] deep-link target not yet in contacts list', { focusId, focusPhone, loaded: calls.length });
      return;
    }
    // Accept this focus key up-front (not after DOM hit) so re-renders / the URL
    // strip don't re-run the body for the same notification.
    lastFocusKeyRef.current = focusKey;
    // The DOM row id is keyed on contactId; resolve it from the matched lead so the
    // phone-fallback path still scrolls/highlights correctly.
    const rowKey = match.contactId;
    // Clear active filters first so the target row isn't filtered out of the list
    clearAllFilters();
    // Drive the highlight from React state (not an imperative class) so it survives
    // re-renders and, crucially, clears itself after a short window.
    setHighlightedContactId(rowKey);
    // Open the lead drawer for the matched lead. The drawer is a right-side
    // max-w-lg slide-in panel over a bg-black/30 scrim (NOT full-screen), so the
    // highlighted row stays visible behind it — satisfying both 'menu opens with
    // info' AND 'lead located'. `match` carries contactId/name/phone and the
    // drawer auto-fetches the rest. Set after highlight so highlight+scroll fire first.
    setSelectedCall(match);
    // Scroll the row into view after a short render delay. The row may not be in
    // the DOM yet (filter clear / pagination re-render) — retry a few times and
    // only mark handled once we actually find + scroll to the element.
    let attempts = 0;
    let scrollTimer;
    const tryFocus = () => {
      const el = document.getElementById(`contact-row-${rowKey}`);
      if (el) {
        // Scroll into view. The visual highlight is driven declaratively by
        // CallLogTable via the highlightedContactId state (set above), which
        // survives re-renders and self-clears after ~3s. The one-shot guard is
        // already keyed on the focus param value (lastFocusKeyRef set above), so
        // we don't need a DOM-success flag here.
        // Scroll mechanics: clearAllFilters()/setSelectedCall() trigger a re-render
        // that reflows the list and CANCELS an in-flight smooth scroll (which is why
        // the highlight showed but the scroll never landed). Use an INSTANT scroll and
        // defer it past the re-render with a double rAF so it fires the frame after the
        // list settles. (Highlight itself is driven declaratively via
        // highlightedContactId state set above, which survives re-renders.)
        // NOTE: v1 replaced the old `focusHandled` boolean with `lastFocusKeyRef`
        // (claimed up-front above) so repeat notification clicks re-focus.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.scrollIntoView({ behavior: 'auto', block: 'center' });
          });
        });
      } else if (attempts++ < 15) {
        scrollTimer = setTimeout(tryFocus, 200);
      } else {
        console.warn('[NOTIF] deep-link row never rendered for', { rowKey, focusId, focusPhone });
      }
    };
    scrollTimer = setTimeout(tryFocus, 300);
    // Fade the highlight back out after a couple seconds so it's a transient cue,
    // not a permanent ring on the row. Stored on a ref (not a local closed over by
    // cleanup) so an effect re-run from a `calls` update can't cancel the fade —
    // we clear+reset it ourselves on each accepted focus instead.
    if (highlightClearTimerRef.current) clearTimeout(highlightClearTimerRef.current);
    highlightClearTimerRef.current = setTimeout(() => setHighlightedContactId(null), 3000);
    // Strip focusContact/focusPhone from the URL so a refresh / later re-render
    // doesn't re-trigger (or re-stick) the highlight. Replace history, no scroll reset.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('focusContact');
    nextParams.delete('focusPhone');
    // Re-arm for a repeat click of the SAME lead. The strip empties the focus
    // params, so the very next effect re-run (from the searchParams dep below)
    // early-returns on the empty key. But if the user clicks the same lead's
    // notification AGAIN later, searchParams changes back to that key — and the
    // value-guard above (lastFocusKeyRef === focusKey) would dead-no-op it.
    // Reset the ref here so re-clicking the same lead re-focuses.
    lastFocusKeyRef.current = '';
    setSearchParams(nextParams, { replace: true });
    // Cleanup only tears down the (short-lived) scroll retry timer. The highlight
    // clear timer lives on a ref and is intentionally NOT cancelled here, so an
    // effect re-run triggered by a `calls` refresh can't abort the 3s fade.
    return () => {
      // Only clean up the scroll-retry timer here. The 3s highlight-clear timer is
      // owned by highlightClearTimerRef so this cleanup (fired when the URL strip
      // re-runs the effect) can't cancel the highlight early.
      clearTimeout(scrollTimer);
    };
    // Deps: react to data readiness (calls/loading) AND searchParams. The 2nd/Nth
    // click of a notification for an ALREADY-MOUNTED campaign only mutates
    // searchParams (no remount, no calls/loading change) — so without searchParams
    // in deps the effect never re-fires and the click is dead. The empty-key
    // early-return (focusKey falsy) neutralizes the URL-strip re-fire, and the
    // value-keyed guard (lastFocusKeyRef, reset on strip) makes any other
    // incidental re-run a safe no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, loading, searchParams]);

  const filteredCalls = calls.filter(call => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesName = call.name?.toLowerCase().includes(q);
      const matchesPhone = call.phone?.includes(q);
      const matchesOutcome = call.outcome?.toLowerCase().includes(q);
      const matchesOutcomeLabel = call.outcome_label?.toLowerCase().includes(q);
      const matchesSummary = call.outcome_summary?.toLowerCase().includes(q);
      if (!(matchesName || matchesPhone || matchesOutcome || matchesOutcomeLabel || matchesSummary)) return false;
    }
    if (statusFilter !== 'all' && !statusMatchesFilter(call.status, statusFilter)) return false;
    if (outcomeFilter !== 'all') {
      if (outcomeFilter === 'no_outcome') { if (call.outcome) return false; }
      else {
        // Keep legacy appointment keys together without trusting display labels,
        // which may be stale after an outcome is revised.
        const recordedOutcome = typeof call.outcome === 'string' ? call.outcome.trim().toLowerCase().replace(/[\s-]+/g, '_') : call.outcome;
        const canonicalOutcome = recordedOutcome === 'cita_agendada' || recordedOutcome === 'appointment_booked'
          ? 'appointment_scheduled'
          : call.outcome;
        if (canonicalOutcome !== outcomeFilter) return false;
      }
    }
    if (attemptsFilter !== 'all') {
      const att = call.attempts || 0;
      if (attemptsFilter === '0' && att !== 0) return false;
      if (attemptsFilter === '1' && att !== 1) return false;
      if (attemptsFilter === '2' && att !== 2) return false;
      if (attemptsFilter === '3+' && att < 3) return false;
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'newest') return (b.startedAt || '').localeCompare(a.startedAt || '');
    if (sortBy === 'oldest') return (a.startedAt || '').localeCompare(b.startedAt || '');
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'attempts_desc') return (b.attempts || 0) - (a.attempts || 0);
    if (sortBy === 'status') return (a.status || '').localeCompare(b.status || '');
    return 0;
  });

  // Load campaign data from API.
  // StrictMode (dev) double-invokes effects, which previously fired TWO identical
  // "Error al cargar campaña" toasts. Guard so the initial load runs once per id.
  const loadGuardRef = useRef(null);
  useEffect(() => {
    if (loadGuardRef.current === id) return;
    loadGuardRef.current = id;
    const loadCampaign = async () => {
      try {
        setLoading(true);
        const res = await fetchCampaign(id);
        setCampaign(res.data);
        // Load contacts as "calls" for now (until dialer exists)
        try {
          const contactsRes = await listContacts(id, { limit: 500 });
          setCalls((contactsRes.data?.contacts || []).map(c => ({
            id: c.contact_id,
            contactId: c.contact_id,
            phone: c.phone,
            name: c.name || '',
            status: c.status === 'pending' ? 'queued' : c.status,
            outcome: c.last_outcome || c.latest_outcome || null,
            outcome_label: c.outcome_data?.outcome_label || null,
            outcome_summary: c.outcome_summary || c.outcome_data?.summary || '',
            duration: resolveContactDuration(c),
            attempts: c.call_attempts || 0,
            maxRetries: res.data?.max_retries || 0,
            startedAt: c.created_at,
            usedCallerId: c.call_history?.length ? c.call_history[c.call_history.length - 1]?.caller_id || '' : '',
          })));
        } catch (contactsErr) {
          // A 404 = no contacts yet (benign). Any other status = a real error
          // (e.g. cross-tenant gate 404 vs auth/500) and must NOT look like
          // "empty" — log + surface it so empty-due-to-error is distinguishable.
          const cStatus = contactsErr?.response?.status;
          if (cStatus && cStatus !== 404) {
            console.error('Failed to load campaign contacts:', contactsErr);
            toast.error(t('errors.failedLoadCampaign'));
          }
        }
      } catch (err) {
        // Surface the real HTTP status so an access gate (403/404) is
        // distinguishable from a transient/server error in logs.
        const status = err?.response?.status;
        console.error(`Failed to load campaign (status=${status ?? 'n/a'}):`, err);
        toast.error(t('errors.failedLoadCampaign'));
      } finally {
        setLoading(false);
      }
    };
    loadCampaign();
  }, [id]);

  // Fetch per-tenant outcome options so the filter dropdown reflects the tenant's
  // analytics_layout (sales vs debt_collection) instead of the hardcoded full list.
  useEffect(() => {
    let cancelled = false;
    getTenantOutcomeOptions()
      .then(res => { if (!cancelled) setOutcomeOptions(res.data?.options || null); })
      .catch(() => { /* non-fatal: dropdown falls back to static callOutcomes */ });
    return () => { cancelled = true; };
  }, []);

  // Auto-refresh when campaign is active (running or paused with pending retries)
  useEffect(() => {
    if (!campaign) return;
    const isActive = campaign.status === 'running' || campaign.status === 'calling';
    if (!isActive) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetchCampaign(id);
        setCampaign(res.data);
        const contactsRes = await listContacts(id, { limit: 500 });
        setCalls((contactsRes.data?.contacts || []).map(c => ({
          id: c.contact_id,
          contactId: c.contact_id,
          phone: c.phone,
          name: c.name || '',
          status: c.status === 'pending' ? 'queued' : c.status,
          outcome: c.last_outcome || c.latest_outcome || null,
          outcome_label: c.outcome_data?.outcome_label || null,
          outcome_summary: c.outcome_summary || c.outcome_data?.summary || '',
          duration: resolveContactDuration(c),
          attempts: c.call_attempts || 0,
          maxRetries: res.data?.max_retries || 0,
          startedAt: c.created_at,
        })));
      } catch (err) {
        console.error('Auto-refresh error:', err);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(interval);
  }, [id, campaign?.status]);

  // Action handlers
  const handleStart = async () => {
    try {
      await updateCampaignStatus(id, 'running');
      setCampaign(prev => ({ ...prev, status: 'running' }));
      toast.success(t('success.campaignStarted'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.failedStartCampaign'));
    }
  };

  const handlePause = async () => {
    try {
      await updateCampaignStatus(id, 'paused');
      setCampaign(prev => ({ ...prev, status: 'paused' }));
      toast.success(t('success.campaignPaused'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.failedPauseCampaign'));
    }
  };

  const handleResume = async () => {
    try {
      await updateCampaignStatus(id, 'running');
      setCampaign(prev => ({ ...prev, status: 'running' }));
      toast.success(t('success.campaignResumed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.failedResumeCampaign'));
    }
  };

  const handleStop = async () => {
    try {
      await updateCampaignStatus(id, 'completed');
      setCampaign(prev => ({ ...prev, status: 'completed' }));
      toast.success(t('success.campaignStopped'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.failedStopCampaign'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('campaigns.deleteConfirm', { name: campaign.name }))) return;
    try {
      await deleteCampaign(id);
      toast.success(t('success.campaignDeleted'));
      navigate('/campaigns');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.failedDeleteCampaign'));
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportCampaignCallLog(id);
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const fallbackFilename = `campaign-${id}-call-log.xlsx`;
      const filename = filenameMatch?.[1] || fallbackFilename;

      const blob = new Blob([
        response.data,
      ], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Call log export failed:', err);

      let errorMessage = err.response?.data?.detail;
      if (!errorMessage && err.response?.data instanceof Blob && err.response.data.type?.includes('application/json')) {
        try {
          const jsonText = await err.response.data.text();
          const parsed = JSON.parse(jsonText);
          errorMessage = parsed?.detail;
        } catch {
          // ignore JSON parse errors; fallback below
        }
      }

      toast.error(errorMessage || t('errors.failedFetchData'));
    }
  };

  // Refetch campaign + contacts so status (completed->running) and counts update
  const refetchCampaignData = async () => {
    try {
      const res = await fetchCampaign(id);
      setCampaign(res.data);
      const contactsRes = await listContacts(id, { limit: 500 });
      setCalls((contactsRes.data?.contacts || []).map(c => ({
        id: c.contact_id,
        contactId: c.contact_id,
        phone: c.phone,
        name: c.name || '',
        status: c.status === 'pending' ? 'queued' : c.status,
        outcome: c.last_outcome || c.latest_outcome || null,
        outcome_label: c.outcome_data?.outcome_label || null,
        outcome_summary: c.outcome_summary || c.outcome_data?.summary || '',
        duration: resolveContactDuration(c),
        attempts: c.call_attempts || 0,
        maxRetries: res.data?.max_retries || 0,
        startedAt: c.created_at,
        usedCallerId: c.call_history?.length ? c.call_history[c.call_history.length - 1]?.caller_id || '' : '',
      })));
    } catch (err) {
      console.error('Refetch after add-leads failed:', err);
    }
  };

  const handleAddLeadsClick = () => {
    if (uploadingLeads) return;
    addLeadsInputRef.current?.click();
  };

  const handleAddLeadsFile = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file again re-triggers onChange.
    if (e.target) e.target.value = '';
    if (!file) return;
    setUploadingLeads(true);
    try {
      // Reuse the existing wizard uploader + endpoint (POST /campaigns/{id}/contacts/upload).
      const res = await uploadContactsFile(id, file);
      const data = res?.data || {};
      // Backend response field names may vary across deploys; fall back across known shapes.
      const added = data.added ?? data.contacts_added ?? data.new_contacts ?? 0;
      const skipped = data.skipped_existing ?? data.skipped ?? data.duplicates ?? 0;
      toast.success(t('campaigns.addLeadsSuccess', {
        added,
        skipped,
        defaultValue: '{{added}} leads nuevos agregados, {{skipped}} repetidos omitidos',
      }));
      await refetchCampaignData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('campaigns.addLeadsError', { defaultValue: 'No se pudieron agregar los leads' }));
    } finally {
      setUploadingLeads(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-secondary rounded w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-28 bg-secondary rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-secondary rounded-xl" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-16">
        <ExclamationCircleIcon className="w-12 h-12 mx-auto text-muted mb-4" />
        <h2 className="text-lg font-medium text-foreground mb-2">{t('campaigns.campaignNotFound')}</h2>
        <p className="text-sm text-muted mb-6">{t('campaigns.campaignNotFoundDesc')}</p>
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-full hover:bg-primary-hover"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t('campaigns.backToCampaigns')}
        </Link>
      </div>
    );
  }

  const callsPlaced = campaign.calls_placed || 0;
  const callsConnected = campaign.calls_connected || 0;
  const callsVoicemail = campaign.calls_voicemail || 0;
  const callsNoAnswer = campaign.calls_no_answer || 0;
  const callsFailed = campaign.calls_failed || 0;
  // crm_dial_whitelist pilot skips: real leads NEVER dialed. Surfaced as a distinct
  // chip, excluded from Realizadas and from the connection-rate denominator so the
  // rate reflects REAL attempts (fix-campaign-skipped-pilot-labels-sep3).
  const callsSkippedPilot = campaign.calls_skipped_pilot || 0;
  const totalContacts = campaign.total_contacts || 0;
  const avgDuration = campaign.avg_duration || 0;
  // Denominator is real dial attempts (callsPlaced already excludes pilot skips).
  const connectionRate = callsPlaced > 0
    ? Math.round((callsConnected / callsPlaced) * 100)
    : 0;
  // Prefer the read-time display status (e.g. 'piloto') over the raw stored status
  // so a campaign that only pilot-skipped real leads does not read "Completada".
  const displayStatus = campaign.display_status || campaign.status;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/campaigns"
            className="p-2 hover:bg-hover rounded-full transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-muted" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-foreground">{campaign.name}</h1>
              <StatusBadge status={displayStatus} t={t} />
              {callsSkippedPilot > 0 && (
                <span
                  title={t('campaigns.skippedPilotTooltip', { defaultValue: 'No se marcaron: números fuera de la lista blanca de prueba (piloto).' })}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-400"
                >
                  {t('campaigns.skippedPilotChip', { count: callsSkippedPilot, defaultValue: `Omitidos (piloto): ${callsSkippedPilot}` })}
                </span>
              )}
            </div>
            {campaign.description && (
              <p className="text-sm text-muted mt-1">{campaign.description}</p>
            )}
            {Array.isArray(campaign.caller_ids) && campaign.caller_ids.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {campaign.caller_ids.map((num) => (
                  <span key={num} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 text-primary text-xs font-medium">
                    <PhoneIcon className="w-3 h-3" />
                    {num}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
            <button
              onClick={handleStart}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <PlayIcon className="w-4 h-4" />
              {t('campaigns.start')}
            </button>
          )}
          {campaign.status === 'running' && (
            <button
              onClick={handlePause}
              className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-full hover:bg-hover transition-colors text-sm font-medium"
            >
              <PauseIcon className="w-4 h-4" />
              {t('campaigns.pause')}
            </button>
          )}
          {campaign.status === 'paused' && (
            <button
              onClick={handleResume}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <PlayIcon className="w-4 h-4" />
              {t('campaigns.resume')}
            </button>
          )}
          {(campaign.status === 'running' || campaign.status === 'paused') && (
            <button
              onClick={handleStop}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-full hover:bg-red-50 transition-colors text-sm font-medium"
            >
              <StopIcon className="w-4 h-4" />
              {t('campaigns.stop')}
            </button>
          )}
          <input
            ref={addLeadsInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.json"
            onChange={handleAddLeadsFile}
            className="hidden"
          />
          <button
            onClick={handleAddLeadsClick}
            disabled={uploadingLeads}
            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-full hover:bg-hover transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlusIcon className="w-4 h-4" />
            {uploadingLeads
              ? t('campaigns.addLeadsUploading', { defaultValue: 'Agregando…' })
              : t('campaigns.addLeads', { defaultValue: 'Agregar leads' })}
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-full hover:bg-hover transition-colors text-sm font-medium"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {t('common.export')}
          </button>
          {campaign.status === 'draft' && (
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-full hover:bg-red-50 transition-colors text-sm font-medium"
            >
              <TrashIcon className="w-4 h-4" />
              {t('common.delete')}
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          icon={PhoneIcon}
          label={t('campaigns.callsPlacedLabel')}
          value={callsPlaced.toLocaleString()}
          subValue={t('campaigns.ofTotal', { count: totalContacts.toLocaleString() })}
          color="#1a73e8"
        />
        <KPICard
          icon={CheckCircleIcon}
          label={t('campaigns.connectedLabel')}
          value={callsConnected.toLocaleString()}
          subValue={t('campaigns.connectionRate', { rate: connectionRate })}
          color="#16a34a"
        />
        <KPICard
          icon={UserIcon}
          label={t('campaigns.voicemailLabel')}
          value={callsVoicemail.toLocaleString()}
          color="#9333ea"
        />
        <KPICard
          icon={PhoneXMarkIcon}
          label={t('campaigns.noAnswerLabel')}
          value={callsNoAnswer.toLocaleString()}
          color="#f59e0b"
        />
        <KPICard
          icon={ExclamationCircleIcon}
          label={t('campaigns.failedLabel')}
          value={callsFailed.toLocaleString()}
          color="#dc2626"
        />
        <KPICard
          icon={ClockIcon}
          label={t('campaigns.avgDurationLabel')}
          value={formatDuration(avgDuration)}
          color="#0ea5e9"
        />
        {callsSkippedPilot > 0 && (
          <KPICard
            icon={NoSymbolIcon}
            label={t('campaigns.skippedPilotLabel', { defaultValue: 'Omitidos (piloto)' })}
            value={callsSkippedPilot.toLocaleString()}
            subValue={t('campaigns.skippedPilotSub', { defaultValue: 'Fuera de lista blanca — no marcados' })}
            color="#6366f1"
          />
        )}
      </div>

      {/* Progress Bar */}
      <CampaignProgress
        current={callsPlaced}
        total={totalContacts}
        status={campaign.status}
        t={t}
      />

      {/* Call Log */}
      <div className="bg-card rounded-xl border border-border">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{t('campaigns.callLog')}</h2>
            <p className="text-sm text-muted">
              {campaign.status === 'running' ? t('campaigns.liveUpdates') : t('campaigns.clickCallDetails')}
            </p>
          </div>
          {campaign.status === 'running' && (
            <span className="inline-flex items-center gap-1.5 text-xs text-green-600">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              {t('campaigns.live')}
            </span>
          )}
        </div>
        {/* Filters */}
        <div className="px-6 py-3 border-b border-border space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('campaigns.searchContactsPlaceholder')}
                className="w-full pl-9 pr-3 py-1.5 rounded-full bg-card border border-border text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 rounded-full bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
              <option value="all">{t('campaigns.allStatuses')}</option>
              {STATUS_FILTER_OPTIONS.map(({ key, labelKey }) => (
                <option key={key} value={key}>{t(labelKey)}</option>
              ))}
            </select>
            <select
              value={outcomeFilter}
              onChange={e => setOutcomeFilter(e.target.value)}
              className="px-3 py-1.5 rounded-full bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
              <option value="all">{t('campaigns.allOutcomes')}</option>
              <option value="no_outcome">{t('campaigns.noOutcome')}</option>
              {outcomeOptions
                ? outcomeOptions.map(({ key, label }) => (
                    <option key={key} value={key}>{t(`callOutcomes.${key}`, { defaultValue: label })}</option>
                  ))
                : Object.entries(callOutcomes).filter(([key]) => key !== 'no_outcome').map(([key]) => (
                    <option key={key} value={key}>{t(callOutcomes[key].labelKey)}</option>
                  ))
              }
            </select>
            <select
              value={attemptsFilter}
              onChange={e => setAttemptsFilter(e.target.value)}
              className="px-3 py-1.5 rounded-full bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
              <option value="all">{t('campaigns.allAttempts')}</option>
              <option value="0">{t('campaigns.noAttempts')}</option>
              <option value="1">1 {t('campaigns.attempt')}</option>
              <option value="2">2 {t('campaigns.attempts')}</option>
              <option value="3+">3+ {t('campaigns.attempts')}</option>
            </select>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-3 py-1.5 rounded-full bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
              <option value="newest">{t('campaigns.sortNewest')}</option>
              <option value="oldest">{t('campaigns.sortOldest')}</option>
              <option value="name">{t('campaigns.sortName')}</option>
              <option value="attempts_desc">{t('campaigns.sortAttempts')}</option>
              <option value="status">{t('campaigns.sortStatus')}</option>
            </select>
          </div>
          {/* Active filters + count */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted whitespace-nowrap">
              {filteredCalls.length === calls.length
                ? `${calls.length} ${t('campaigns.contacts')}`
                : `${filteredCalls.length} / ${calls.length}`}
            </span>
            {hasActiveFilters && (
              <>
                {searchQuery && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    "{searchQuery}"
                    <button onClick={() => setSearchQuery('')} className="hover:text-primary/70"><XMarkIcon className="w-3 h-3" /></button>
                  </span>
                )}
                {statusFilter !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    {t(callStatuses[statusFilter]?.labelKey || statusFilter)}
                    <button onClick={() => setStatusFilter('all')} className="hover:text-primary/70"><XMarkIcon className="w-3 h-3" /></button>
                  </span>
                )}
                {outcomeFilter !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    {outcomeFilter === 'no_outcome' ? t('campaigns.noOutcome') : t(callOutcomes[outcomeFilter]?.labelKey || outcomeFilter)}
                    <button onClick={() => setOutcomeFilter('all')} className="hover:text-primary/70"><XMarkIcon className="w-3 h-3" /></button>
                  </span>
                )}
                {attemptsFilter !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    {attemptsFilter === '0' ? t('campaigns.noAttempts') : `${attemptsFilter} ${t('campaigns.attempts')}`}
                    <button onClick={() => setAttemptsFilter('all')} className="hover:text-primary/70"><XMarkIcon className="w-3 h-3" /></button>
                  </span>
                )}
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-muted hover:text-foreground transition-colors underline"
                >
                  {t('campaigns.clearFilters')}
                </button>
              </>
            )}
          </div>
        </div>
        <CallLogTable
          calls={filteredCalls}
          hasCampaignCalls={calls.length > 0}
          onCallClick={setSelectedCall}
          loading={loading}
          highlightedContactId={highlightedContactId}
          selectedContactId={selectedCall?.contactId}
          t={t}
        />
      </div>

      {/* Call Detail Drawer */}
      {selectedCall && (
        <CallDetailDrawer
          call={selectedCall}
          campaignId={id}
          onClose={() => setSelectedCall(null)}
          t={t}
        />
      )}
    </div>
  );
}

export default CampaignDetail;
